import React, { useState } from 'react';
import {
  Paper,
  Title,
  Group,
  Button,
  Table,
  ActionIcon,
  Badge,
  Text,
  TextInput,
  LoadingOverlay,
  Tooltip,
  Pagination,
  Select,
  Stack,

  Menu,
  rem,
  Indicator,
  Modal,
  MultiSelect,
} from '@mantine/core';
import {
  IconPlus,
  IconEdit,
  IconEye,
  IconSearch,
  IconMapPin,
  IconDots,
  IconTrash,

  IconCamera,
  IconUsers,
  IconShield,
  IconBuildingStore,
} from '@tabler/icons-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDebouncedValue } from '@mantine/hooks';

import { useSchoolAreas, useDeleteArea } from '../../hooks/useAreas';
import { useCameras } from '../../hooks/useCameras';
import type { Area } from '../../types/api';

const Areas: React.FC = () => {
  const navigate = useNavigate();
  const { schoolId } = useParams<{ schoolId: string }>();
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [areaTypeFilter, setAreaTypeFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [selectedArea, setSelectedArea] = useState<Area | null>(null);
  const [cameraAssignmentOpen, setCameraAssignmentOpen] = useState(false);

  // Query parameters
  const queryParams = {
    page,
    per_page: perPage,
    search: debouncedSearch || undefined,
    area_type: areaTypeFilter || undefined,
    active: statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined,
  };

  const { data: areasData, isLoading, error } = useSchoolAreas(schoolId || '', queryParams);
  const { data: camerasData } = useCameras({ per_page: 100 });
  const deleteAreaMutation = useDeleteArea();


  const areas = areasData?.areas || [];
  const pagination = areasData?.pagination;
  const cameras = camerasData?.cameras || [];

  const handleViewArea = (area: Area) => {
    navigate(`/admin/areas/${area.id}`);
  };

  const handleEditArea = (area: Area) => {
    navigate(`/admin/areas/${area.id}/edit`);
  };

  const handleDeleteArea = async (area: Area) => {
    if (window.confirm(`Are you sure you want to delete "${area.name}"?`)) {
      try {
        await deleteAreaMutation.mutateAsync(parseInt(area.id));
      } catch (error) {
        // Error handling is done in the mutation
      }
    }
  };

  const handleManageCameras = (area: Area) => {
    setSelectedArea(area);
    setCameraAssignmentOpen(true);
  };

  const getAreaTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      classroom: 'blue',
      playground: 'green',
      cafeteria: 'orange',
      hallway: 'gray',
      office: 'purple',
      entrance: 'red',
      library: 'teal',
      gymnasium: 'yellow',
    };
    return colors[type] || 'gray';
  };

  const getAreaTypeIcon = (type: string) => {
    const icons: Record<string, React.ReactNode> = {
      classroom: <IconUsers size={14} />,
      playground: <IconMapPin size={14} />,
      cafeteria: <IconBuildingStore size={14} />,
      hallway: <IconMapPin size={14} />,
      office: <IconBuildingStore size={14} />,
      entrance: <IconMapPin size={14} />,
      library: <IconBuildingStore size={14} />,
      gymnasium: <IconUsers size={14} />,
    };
    return icons[type] || <IconMapPin size={14} />;
  };

  if (error) {
    return (
      <Paper p="md">
        <Text c="red">Error loading areas: {(error as any)?.message || 'Unknown error'}</Text>
      </Paper>
    );
  }

  return (
    <Stack gap="md">
      {/* Header */}
      <Paper p="md">
        <Group justify="space-between" align="center">
          <div>
            <Title order={2}>Areas Management</Title>
            <Text size="sm" c="dimmed">
              Manage school areas and camera assignments
            </Text>
          </div>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => navigate('/admin/areas/new')}
          >
            Add Area
          </Button>
        </Group>
      </Paper>

      {/* Filters */}
      <Paper p="md">
        <Group gap="md">
          <TextInput
            placeholder="Search areas..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Area Type"
            data={[
              { value: 'classroom', label: 'Classroom' },
              { value: 'playground', label: 'Playground' },
              { value: 'cafeteria', label: 'Cafeteria' },
              { value: 'hallway', label: 'Hallway' },
              { value: 'office', label: 'Office' },
              { value: 'entrance', label: 'Entrance' },
              { value: 'library', label: 'Library' },
              { value: 'gymnasium', label: 'Gymnasium' },
            ]}
            value={areaTypeFilter}
            onChange={setAreaTypeFilter}
            clearable
          />
          <Select
            placeholder="Status"
            data={[
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ]}
            value={statusFilter}
            onChange={setStatusFilter}
            clearable
          />
          <Select
            placeholder="Per page"
            data={[
              { value: '10', label: '10' },
              { value: '20', label: '20' },
              { value: '50', label: '50' },
            ]}
            value={perPage.toString()}
            onChange={(value) => setPerPage(parseInt(value || '20'))}
          />
        </Group>
      </Paper>

      {/* Areas Table */}
      <Paper p="md" pos="relative">
        <LoadingOverlay visible={isLoading} />

        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Type</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Supervision</Table.Th>
              <Table.Th>Cameras</Table.Th>
              <Table.Th>Description</Table.Th>
              <Table.Th style={{ width: 100 }}>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {areas.map((area) => (
              <Table.Tr key={area.id}>
                <Table.Td>
                  <Group gap="xs">
                    {getAreaTypeIcon(area.area_type)}
                    <Text fw={500}>{area.name}</Text>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Badge
                    color={getAreaTypeColor(area.area_type)}
                    variant="light"
                    size="sm"
                  >
                    {area.area_type}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Indicator
                    color={area.active ? 'green' : 'red'}
                    size={8}
                    offset={7}
                    position="middle-start"
                  >
                    <Badge
                      color={area.active ? 'green' : 'red'}
                      variant="light"
                      size="sm"
                    >
                      {area.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </Indicator>
                </Table.Td>
                <Table.Td>
                  {(area as any).requires_supervision ? (
                    <Badge color="orange" variant="light" size="sm" leftSection={<IconShield size={12} />}>
                      Required
                    </Badge>
                  ) : (
                    <Badge color="gray" variant="light" size="sm">
                      Optional
                    </Badge>
                  )}
                </Table.Td>
                <Table.Td>
                  <Button
                    variant="light"
                    size="xs"
                    leftSection={<IconCamera size={12} />}
                    onClick={() => handleManageCameras(area)}
                  >
                    Manage
                  </Button>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed" lineClamp={2}>
                    {area.description || 'No description'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    <Tooltip label="View Details">
                      <ActionIcon
                        variant="subtle"
                        color="blue"
                        onClick={() => handleViewArea(area)}
                      >
                        <IconEye style={{ width: rem(16), height: rem(16) }} />
                      </ActionIcon>
                    </Tooltip>
                    <Menu shadow="md" width={200}>
                      <Menu.Target>
                        <ActionIcon variant="subtle" color="gray">
                          <IconDots style={{ width: rem(16), height: rem(16) }} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item
                          leftSection={<IconEdit style={{ width: rem(14), height: rem(14) }} />}
                          onClick={() => handleEditArea(area)}
                        >
                          Edit Area
                        </Menu.Item>
                        <Menu.Item
                          leftSection={<IconCamera style={{ width: rem(14), height: rem(14) }} />}
                          onClick={() => handleManageCameras(area)}
                        >
                          Manage Cameras
                        </Menu.Item>
                        <Menu.Divider />
                        <Menu.Item
                          leftSection={<IconTrash style={{ width: rem(14), height: rem(14) }} />}
                          color="red"
                          onClick={() => handleDeleteArea(area)}
                          disabled={deleteAreaMutation.isPending}
                        >
                          Delete Area
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>

        {areas.length === 0 && !isLoading && (
          <Text ta="center" py="xl" c="dimmed">
            No areas found
          </Text>
        )}
      </Paper>

      {/* Pagination */}
      {pagination && pagination.total_pages > 1 && (
        <Group justify="center">
          <Pagination
            value={page}
            onChange={setPage}
            total={pagination.total_pages}
          />
        </Group>
      )}

      {/* Camera Assignment Modal */}
      <Modal
        opened={cameraAssignmentOpen}
        onClose={() => setCameraAssignmentOpen(false)}
        title={`Manage Cameras - ${selectedArea?.name}`}
        size="lg"
      >
        {selectedArea && (
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              Assign cameras to monitor this area
            </Text>

            <MultiSelect
              label="Available Cameras"
              placeholder="Select cameras to assign"
              data={cameras.map(camera => ({
                value: camera.id,
                label: `${camera.name} (${(camera as any).location?.area?.name || 'No area'})`,
              }))}
              searchable
              clearable
            />

            <Group justify="flex-end">
              <Button variant="outline" onClick={() => setCameraAssignmentOpen(false)}>
                Cancel
              </Button>
              <Button onClick={() => setCameraAssignmentOpen(false)}>
                Save Changes
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
};

export default Areas;
